/**
 * Resource levelling for the Gantt chart.
 *
 * Given each task's assignee, effort estimate, and each person's weekly
 * capacity, serialises every assignee's work so they aren't scheduled to do
 * more than they can at once: an assignee's tasks are packed back-to-back from
 * an anchor date, in priority order, each occupying `ceil(estimate /
 * dailyCapacity)` days. Tasks with a **locked** start date are treated as fixed
 * obstacles — never moved, and other tasks schedule around them.
 *
 * v1 simplifications (documented; safe to refine later):
 * - Calendar days (weekends not skipped).
 * - A task with multiple assignees is levelled against its FIRST assignee.
 * - Done tasks and tasks without an estimate or assignee are left untouched.
 *
 * Pure + deterministic given an explicit `anchor`, so it is unit-testable. The
 * caller (browser) supplies today's date as the anchor default.
 */

export type LevelTask = {
  id: string;
  title: string;
  assigneeIds: string[];
  estimate: number | null;
  startDate: string | null; // YYYY-MM-DD
  dueDate: string | null;
  priority: number; // 1=urgent … 4=low
  status: string;
  startLocked?: boolean | number;
};

export type LevelProposal = {
  id: string;
  title: string;
  assigneeId: string;
  fromStart: string | null;
  fromDue: string | null;
  toStart: string;
  toDue: string;
  durationDays: number;
};

const MS_PER_DAY = 86_400_000;

/** YYYY-MM-DD → integer day index (UTC midnight), avoiding TZ drift. */
function dayIndex(s: string): number {
  const [y, m, d] = s.split("-").map(Number);
  return Math.floor(Date.UTC(y!, m! - 1, d!) / MS_PER_DAY);
}
function fromDayIndex(n: number): string {
  const dt = new Date(n * MS_PER_DAY);
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dt.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

type Interval = { start: number; end: number }; // inclusive day indices

/** Earliest start >= `from` for a `dur`-day block not overlapping `locked`. */
function earliestSlot(from: number, dur: number, locked: Interval[]): number {
  let start = from;
  // Locked intervals are sorted by start; walk forward past any overlap.
  let moved = true;
  while (moved) {
    moved = false;
    const end = start + dur - 1;
    for (const iv of locked) {
      if (start <= iv.end && end >= iv.start) {
        start = iv.end + 1;
        moved = true;
        break;
      }
    }
  }
  return start;
}

export function levelResources(
  tasks: LevelTask[],
  capacityByUser: Map<string, number | null>,
  opts: { defaultWeeklyCapacity?: number; anchor: string },
): LevelProposal[] {
  const DEFAULT_WEEKLY = opts.defaultWeeklyCapacity ?? 40;
  const anchor = dayIndex(opts.anchor);

  // Eligible = assigned, estimable, not done.
  const eligible = tasks.filter(
    (t) =>
      t.status !== "done" && t.assigneeIds.length > 0 && (t.estimate ?? 0) > 0,
  );

  // Group by primary assignee.
  const byUser = new Map<string, LevelTask[]>();
  for (const t of eligible) {
    const u = t.assigneeIds[0]!;
    (byUser.get(u) ?? byUser.set(u, []).get(u)!).push(t);
  }

  const proposals: LevelProposal[] = [];

  for (const [userId, userTasks] of byUser) {
    const weekly = capacityByUser.get(userId) ?? DEFAULT_WEEKLY;
    const dailyCap = Math.max(0.1, (weekly || DEFAULT_WEEKLY) / 5);
    const duration = (t: LevelTask) =>
      Math.max(1, Math.ceil((t.estimate ?? 0) / dailyCap));

    // Locked tasks with a real start are fixed obstacles.
    const locked: Interval[] = userTasks
      .filter((t) => Boolean(t.startLocked) && t.startDate)
      .map((t) => {
        const s = dayIndex(t.startDate!);
        return { start: s, end: s + duration(t) - 1 };
      })
      .sort((a, b) => a.start - b.start);

    // Schedule the unlocked tasks, priority then current start then title.
    // A locked task is never scheduled (and never proposed) — if it also has a
    // start date it acts as a fixed obstacle above; if not, it's just left be.
    const unlocked = userTasks
      .filter((t) => !t.startLocked)
      .sort(
        (a, b) =>
          a.priority - b.priority ||
          (a.startDate ? dayIndex(a.startDate) : Infinity) -
            (b.startDate ? dayIndex(b.startDate) : Infinity) ||
          a.title.localeCompare(b.title),
      );

    let cursor = anchor;
    for (const t of unlocked) {
      const dur = duration(t);
      const start = earliestSlot(cursor, dur, locked);
      const end = start + dur - 1;
      cursor = end + 1;
      const toStart = fromDayIndex(start);
      const toDue = fromDayIndex(end);
      if (toStart !== t.startDate || toDue !== t.dueDate) {
        proposals.push({
          id: t.id,
          title: t.title,
          assigneeId: userId,
          fromStart: t.startDate,
          fromDue: t.dueDate,
          toStart,
          toDue,
          durationDays: dur,
        });
      }
    }
  }

  return proposals;
}
